import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Smartphone, LogIn, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useToast } from "../hooks/use-toast";
import { login } from "../api";
import useBranding from "../hooks/useBranding";

const formatError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const b = useBranding();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(identifier, password);
      localStorage.removeItem("pp_admin_token");
      localStorage.removeItem("pp_merchant_token");
      localStorage.setItem("pp_role", res.role);
      localStorage.setItem("pp_email", res.email);
      if (res.name) localStorage.setItem("pp_name", res.name);
      if (res.role === "admin") {
        localStorage.setItem("pp_admin_token", res.token);
        toast({ title: "Welcome back 👋" });
        navigate("/admin");
      } else {
        localStorage.setItem("pp_merchant_token", res.token);
        toast({ title: `Welcome, ${res.name || "Partner"} 👋` });
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20";

  return (
    <div className="min-h-screen bg-[#f6f9fd]">
      <Header />
      <section className="pt-[130px] pb-24">
        <div className="pp-container max-w-md">
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-[0_30px_60px_-30px_rgba(13,27,42,0.35)] border border-slate-100">
            <span className="text-[#06B6D4] font-semibold uppercase tracking-wider text-xs">Partner Portal</span>
            <h1 className="mt-2 text-2xl font-extrabold text-[#0D1B2A]">Sign in to {b.company_name}</h1>
            <p className="text-sm text-[#475569] mt-1">Retailers &amp; administrators log in here.</p>

            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div className="relative">
                <Smartphone size={17} className="absolute left-4 top-3.5 text-[#64748B]" />
                <input data-testid="login-email-input" className={field} type="text" placeholder="Mobile Number / Email" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />
              </div>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-3.5 text-[#64748B]" />
                <input data-testid="login-password-input" className={field} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
              <button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-xl disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                {loading ? "Signing in…" : "Sign In"} <LogIn size={16} />
              </button>
            </form>

            <div className="mt-6 text-sm text-[#475569] text-center">
              New merchant?{" "}
              <Link to="/signup" className="font-semibold text-[#0EA5E9] inline-flex items-center gap-1 hover:text-[#06B6D4]">
                Apply for an account <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Login;
